import { useEffect, useMemo, useState } from 'react';
import {
  identityApiRef,
  useApi,
  useApiHolder,
} from '@backstage/frontend-plugin-api';
import {
  catalogApiRef,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import { visitsApiRef, type Visit } from '@backstage/plugin-home';
import type { Entity } from '@backstage/catalog-model';
import {
  Page,
  Header,
  Content,
  Progress,
  ResponseErrorPanel,
  Link,
} from '@backstage/core-components';
import {
  Box,
  Card,
  Chip,
  CardContent,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@material-ui/icons/Search';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import LayersOutlinedIcon from '@material-ui/icons/LayersOutlined';
import LayersIcon from '@material-ui/icons/Layers';
import HistoryIcon from '@material-ui/icons/History';
import { MarketplaceIcon } from '../marketplace/MarketplaceIcon';
import { STATUS_COLOUR, STATUS_LABEL, useMyRuns } from '../requests/useMyRuns';
import {
  entityPath,
  firstName,
  greeting,
  ownedOfKind,
  relativeTime,
  visitLabel,
} from './homeData';
import { EntityTile } from './EntityTile';

/** How many tiles a strip shows before it defers to its "view all" link. */
const STRIP_CAP = 8;
const VISIT_CAP = 6;
const RUN_CAP = 5;

/** What "Yours" is a list of: the things we build and run. */
const MEMBER_KINDS = new Set(['Component', 'API', 'Resource']);

/**
 * Every kind this page renders, and nothing else.
 *
 * The query was previously unfiltered, so it also pulled User, Group and
 * Location — none of which appear anywhere on the page. That is the part that
 * scales badly: GitHub org ingestion turns every employee into a User entity,
 * so an unfiltered home page would grow with headcount.
 */
// System for starred systems. Domain (clients, until D-33) and Template (the
// old "Start something new" strip, now Create's job) are no longer shown.
const HOME_KINDS = [...MEMBER_KINDS, 'System'];

const useStyles = makeStyles(theme => ({
  /* --- Search: the widest single target on the page --- */
  search: { marginBottom: 26 },
  searchInput: {
    fontSize: '0.9375rem',
    backgroundColor: 'var(--ar-surface)',
  },

  /* --- Quick actions --- */
  actionLink: { display: 'block', height: '100%', textDecoration: 'none' },
  action: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 16px',
    transition: 'border-color 120ms ease, background-color 120ms ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: 'var(--ar-surface-2)',
    },
  },
  actionIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 34,
    height: 34,
    flex: '0 0 auto',
    borderRadius: 9,
    backgroundColor: 'var(--ar-accent-soft)',
    color: theme.palette.primary.main,
  },
  actionText: { minWidth: 0 },
  actionTitle: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 620,
    letterSpacing: '-0.011em',
    color: 'var(--ar-fg)',
  },
  actionSub: {
    display: 'block',
    fontSize: '0.75rem',
    color: 'var(--ar-fg-3)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  /* --- Section furniture, shared by every strip --- */
  section: { marginTop: 30 },
  sectionHead: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 12,
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: '0.8125rem',
    fontWeight: 660,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'var(--ar-fg-2)',
  },
  // Sits directly after its heading, NOT pushed to the far edge. With
  // marginLeft:auto it travelled the whole width of the column and landed
  // beside the next column's heading, reading as if it belonged to that one.
  sectionMore: {
    fontSize: '0.8125rem',
    whiteSpace: 'nowrap',
  },
  hint: {
    fontSize: '0.8125rem',
    color: 'var(--ar-fg-3)',
  },

  /* --- Your requests --- */
  runBody: { padding: '6px 0 !important' },
  run: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '9px 16px',
    textDecoration: 'none',
    '&:hover': { backgroundColor: 'var(--ar-surface-2)' },
  },
  runTarget: {
    flex: 1,
    minWidth: 0,
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: 'var(--ar-fg)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  runKind: {
    fontSize: '0.75rem',
    color: 'var(--ar-fg-3)',
    whiteSpace: 'nowrap',
  },

  /* --- Recently visited: a list, not a grid. Order is the information. --- */
  visitBody: { padding: '6px 0 !important' },
  visit: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '9px 16px',
    textDecoration: 'none',
    '&:hover': { backgroundColor: 'var(--ar-surface-2)' },
  },
  visitIcon: { color: 'var(--ar-fg-3)', display: 'flex' },
  visitName: {
    flex: 1,
    minWidth: 0,
    fontSize: '0.8125rem',
    color: theme.palette.primary.main,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  visitAge: {
    fontSize: '0.6875rem',
    color: 'var(--ar-fg-3)',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },

  /* --- Client cards --- */
}));

/**
 * The first row: the two things Create exists for, then the two places people
 * go to look. Clients were a fifth tile until D-33.
 */
const QUICK_ACTIONS = [
  {
    to: '/create/templates/default/answerrocket-service',
    icon: CloudUploadOutlinedIcon,
    title: 'Deploy a service',
    sub: 'Ship code to Cloud Run',
  },
  {
    to: '/create/templates/new-env',
    icon: LayersOutlinedIcon,
    title: 'New environment',
    sub: 'An AWS account or GCP project',
  },
  {
    to: '/browse',
    icon: LayersIcon,
    title: 'Catalog',
    sub: 'Everything we build and run',
  },
  {
    to: '/marketplace',
    icon: MarketplaceIcon,
    title: 'Marketplace',
    sub: 'Claude plugins and skills',
  },
];

/** A strip heading with an optional "view all" on the right. */
const SectionHead = ({
  title,
  moreLabel,
  moreTo,
}: {
  title: string;
  moreLabel?: string;
  moreTo?: string;
}) => {
  const classes = useStyles();
  return (
    <Box className={classes.sectionHead}>
      <span className={classes.sectionTitle}>{title}</span>
      {moreTo && (
        <Link to={moreTo} className={classes.sectionMore}>
          {moreLabel}
        </Link>
      )}
    </Box>
  );
};

/**
 * The portal's landing page.
 *
 * The catalog used to sit here, which assumed every visit starts with a known
 * target to look up. Most do not: they start with "what am I working on",
 * "where do I make a new thing", or "take me back to what I had open". Those
 * three questions are the page, in that order — what is yours on the left
 * where the eye lands, what you asked for and where you were on the right.
 *
 * The right column used to be a client portfolio ("At a glance", "Active
 * clients"). Clients belong to CustomerIQ now (D-33); what a person actually
 * comes back to check is whether the thing they requested has landed.
 */
export const HomePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const { isStarredEntity } = useStarredEntities();
  const myRuns = useMyRuns();

  // The visits API is config-gated (api:home/visits), so it may not be
  // registered at all. useApi would throw in that case; the holder hands back
  // undefined and the section simply does not render.
  const visitsApi = useApiHolder().get(visitsApiRef);

  const [entities, setEntities] = useState<Entity[]>();
  const [error, setError] = useState<Error>();
  const [myRefs, setMyRefs] = useState<string[]>([]);
  const [name, setName] = useState<string>();
  const [visits, setVisits] = useState<Visit[]>([]);
  const [query, setQuery] = useState('');

  // Fixed at mount. Re-reading the clock on every render would make the
  // greeting and every "5m ago" a moving target across re-renders.
  const [now] = useState(() => new Date());

  // One pass over the catalog feeds every section: the same field mask
  // BrowsePage uses, unfiltered so that starred entities of any kind and the
  // scaffolder's Templates come back in the same round trip.
  useEffect(() => {
    let active = true;
    catalogApi
      .getEntities({
        filter: { kind: HOME_KINDS },
        fields: [
          'kind',
          'metadata.name',
          'metadata.namespace',
          'metadata.title',
          'metadata.description',
          'spec.type',
          'spec.lifecycle',
          'spec.owner',
          'spec.system',
        ],
      })
      .then(res => active && setEntities(res.items))
      .catch(e => active && setError(e as Error));
    return () => {
      active = false;
    };
  }, [catalogApi]);

  useEffect(() => {
    let active = true;
    Promise.all([
      identityApi.getBackstageIdentity(),
      identityApi.getProfileInfo(),
    ])
      .then(([id, profile]) => {
        if (!active) return;
        setMyRefs(id.ownershipEntityRefs ?? []);
        setName(firstName(profile.displayName));
      })
      // A missing profile costs the page a first name, nothing more.
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [identityApi]);

  useEffect(() => {
    if (!visitsApi) return undefined;
    let active = true;
    visitsApi
      .list({
        orderBy: [{ field: 'timestamp', direction: 'desc' }],
        // Over-fetched: Home itself and repeat visits are filtered out below,
        // so asking for exactly the cap would show a short list every time.
        limit: VISIT_CAP * 4,
      })
      .then(v => active && setVisits(v))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [visitsApi]);

  const all = useMemo(() => entities ?? [], [entities]);

  // The visit listener records this page too, and "return to the page you are
  // already on" is not a shortcut. It also comes through named after the app
  // rather than the page, so it reads as a stray entry.
  const recent = useMemo(
    () =>
      visits
        .filter(v => v.pathname !== '/')
        // One row per place: visiting the same page three times is still one
        // place to go back to. Keyed on the place, not its label — labels
        // collide (every task page is "Create · task", and a Component and a
        // System can share a name).
        .filter(
          (v, i, list) =>
            list.findIndex(
              o => (o.entityRef ?? o.pathname) === (v.entityRef ?? v.pathname),
            ) === i,
        )
        .slice(0, VISIT_CAP),
    [visits],
  );

  const byName = (a: Entity, b: Entity) =>
    (a.metadata.title ?? a.metadata.name).localeCompare(
      b.metadata.title ?? b.metadata.name,
    );

  const mine = useMemo(
    () =>
      ownedOfKind(all, myRefs, MEMBER_KINDS).sort(byName).slice(0, STRIP_CAP),
    [all, myRefs],
  );

  const starred = useMemo(
    () => all.filter(isStarredEntity).sort(byName).slice(0, STRIP_CAP),
    [all, isStarredEntity],
  );

  // Open requests first — "is it done yet?" is the question — then the most
  // recent of the rest.
  const runs = useMemo(
    () =>
      [...(myRuns.runs ?? [])]
        .sort(
          (a, b) =>
            Number(a.status === 'succeeded') -
              Number(b.status === 'succeeded') ||
            b.updatedAt.localeCompare(a.updatedAt),
        )
        .slice(0, RUN_CAP),
    [myRuns.runs],
  );

  const loading = !entities && !error;
  const hello = name ? `${greeting(now)}, ${name}` : greeting(now);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    // The search page reads its term from the `query` param, so an empty box
    // should land there unfiltered rather than on `?query=`.
    navigate(q ? `/search?query=${encodeURIComponent(q)}` : '/search');
  };

  return (
    <Page themeId="home">
      <Header
        title={hello}
        subtitle="Where you left off, and what to do next"
      />
      <Content>
        <form onSubmit={submitSearch}>
          <TextField
            className={classes.search}
            fullWidth
            variant="outlined"
            placeholder="Search the catalog, docs and templates"
            inputProps={{ 'aria-label': 'Search' }}
            InputProps={{
              className: classes.searchInput,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </form>

        <Grid container spacing={2}>
          {QUICK_ACTIONS.map(({ to, icon: Icon, title, sub }) => (
            <Grid item xs={12} sm={6} md={3} key={to}>
              <Link to={to} className={classes.actionLink}>
                <Card className={classes.action}>
                  <span className={classes.actionIcon}>
                    <Icon fontSize="small" />
                  </span>
                  <span className={classes.actionText}>
                    <span className={classes.actionTitle}>{title}</span>
                    <span className={classes.actionSub}>{sub}</span>
                  </span>
                </Card>
              </Link>
            </Grid>
          ))}
        </Grid>

        {loading && <Progress />}
        {error && <ResponseErrorPanel error={error} />}

        {entities && (
          <Grid container spacing={3} className={classes.section}>
            {/* ---------------- Left: what is yours ---------------- */}
            <Grid item xs={12} md={7}>
              <SectionHead
                title="Yours"
                moreLabel="Browse catalog"
                moreTo="/browse"
              />
              {mine.length === 0 ? (
                <Typography className={classes.hint}>
                  Nothing in the catalog lists you or your team as its owner.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {mine.map(e => (
                    <Grid item xs={12} sm={6} md={4} key={entityPath(e)}>
                      <EntityTile entity={e} />
                    </Grid>
                  ))}
                </Grid>
              )}

              <Box className={classes.section}>
                <SectionHead title="Starred" />
                {starred.length === 0 ? (
                  <Typography className={classes.hint}>
                    Star anything in the catalog and it will wait for you here.
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {starred.map(e => (
                      <Grid item xs={12} sm={6} md={4} key={entityPath(e)}>
                        <EntityTile entity={e} />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Grid>

            {/* ---------------- Right: what you asked for, where you were ---------------- */}
            <Grid item xs={12} md={5}>
              <SectionHead
                title="Your requests"
                moreLabel="All requests"
                moreTo="/create/request"
              />
              {myRuns.loading && <Progress />}
              {myRuns.needsIdentity && (
                <Typography className={classes.hint}>
                  Your requests show here once you are signed in with Google —{' '}
                  <Link to="/create/request">open My requests</Link> to sign in.
                </Typography>
              )}
              {myRuns.error && (
                <Typography className={classes.hint}>
                  Your requests could not be loaded just now —{' '}
                  <Link to="/create/request">open My requests</Link>.
                </Typography>
              )}
              {myRuns.runs?.length === 0 && (
                <Typography className={classes.hint}>
                  Nothing requested yet. Deploying a service or asking for an
                  environment shows up here, from pull request to approval.
                </Typography>
              )}
              {runs.length > 0 && (
                <Card>
                  <CardContent className={classes.runBody}>
                    {runs.map(run => (
                      <Link
                        key={run.id}
                        to="/create/request"
                        className={classes.run}
                      >
                        <span className={classes.runTarget}>{run.target}</span>
                        <span className={classes.runKind}>
                          {relativeTime(
                            Date.parse(run.updatedAt),
                            now.getTime(),
                          )}
                        </span>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={STATUS_LABEL[run.status] ?? run.status}
                          style={{
                            color: STATUS_COLOUR[run.status],
                            borderColor: STATUS_COLOUR[run.status],
                          }}
                        />
                      </Link>
                    ))}
                  </CardContent>
                </Card>
              )}

              {visitsApi && (
                <Box className={classes.section}>
                  <SectionHead title="Recently visited" />
                  {recent.length === 0 ? (
                    <Typography className={classes.hint}>
                      Pages you open will show up here.
                    </Typography>
                  ) : (
                    <Card>
                      <CardContent className={classes.visitBody}>
                        {recent.map(v => (
                          <Link
                            key={v.id}
                            to={v.pathname}
                            className={classes.visit}
                          >
                            <span className={classes.visitIcon}>
                              <HistoryIcon fontSize="small" />
                            </span>
                            <span className={classes.visitName}>
                              {visitLabel(v)}
                            </span>
                            <span className={classes.visitAge}>
                              {relativeTime(v.timestamp, now.getTime())}
                            </span>
                          </Link>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        )}
      </Content>
    </Page>
  );
};
